import { ItemType, ShortcutItem } from '@inno-sdks/files'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { initFilesSdk } from '../filesSdk'
import { getAppFolderId } from '../getAppFolderId'
import { FILES_LIST_QUERY_KEY } from './useFilesList'

type CreateShortcutParams = {
  name: string
  shortcutUrl: string
}

const getAvailableName = (name: string, existingNames: Set<string>) => {
  if (!existingNames.has(name.toLowerCase())) return name

  let suffix = 2
  while (existingNames.has(`${name} (${suffix})`.toLowerCase())) {
    suffix += 1
  }

  return `${name} (${suffix})`
}

export const useCreateShortcutMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ name, shortcutUrl }: CreateShortcutParams) => {
      const { filesSdk, success } = initFilesSdk()

      if (!success || !filesSdk) {
        throw new Error('Not authenticated')
      }

      const appFolderId = await getAppFolderId()
      if (appFolderId == null) {
        throw new Error('Could not resolve desktop folder')
      }

      const existingNames = new Set<string>()
      const visitedCursors = new Set<string>()
      let cursor: string | undefined

      do {
        // Each page supplies the cursor required to fetch the next one.
        // eslint-disable-next-line no-await-in-loop
        const page = await filesSdk.items.getAll({
          filters: { parentFolderId: appFolderId },
          cursor,
          limit: 100
        })
        page.data.forEach(item => existingNames.add(item.name.toLowerCase()))

        if (!page.cursor || visitedCursors.has(page.cursor)) break
        visitedCursors.add(page.cursor)
        cursor = page.cursor
      } while (cursor)

      const availableName = getAvailableName(name, existingNames)
      const [shortcut] = await filesSdk.items.create(
        [{ type: ItemType.Shortcut, name: availableName, shortcutUrl }],
        appFolderId
      )

      return shortcut as ShortcutItem
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [FILES_LIST_QUERY_KEY] })
  })
}
